import React from 'react';
import { motion } from 'framer-motion';
import { fadeInUp, staggerContainer, staggerItem } from '../utils/animations';
import { useTheme, getThemeColors } from '../contexts/ThemeContext';
import Section from './Section';
import Card from './Card';
import LazyImage from './LazyImage';

const Chairman = () => {
  const { isDarkMode } = useTheme();
  const colors = getThemeColors(isDarkMode);

  return (
    <Section id="chairman" className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.3 }}
          className="text-center mb-16"
        >
          <motion.h2
            variants={fadeInUp}
            className={`text-4xl md:text-5xl font-bold ${colors.text.primary} mb-4`}
          >
            Message From <span className="text-primary-400">Chairman</span>
          </motion.h2>
          <motion.div
            variants={fadeInUp}
            className="w-24 h-1 bg-primary-400 mx-auto"
          />
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.2 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center"
        >
          {/* Chairman Image */}
          <motion.div
            variants={staggerItem}
            className="lg:col-span-1 flex justify-center"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-primary-400/20 rounded-2xl blur-2xl scale-110"></div>
              <LazyImage
                src={`${process.env.PUBLIC_URL}/chairman.png`}
                alt="Md. Mahbubur Rashid Jewel - Chairman"
                className="relative z-10 w-96 h-[500px] rounded-2xl shadow-2xl border-4 border-primary-400/30"
                style={{ objectFit: 'cover', objectPosition: 'top' }}
                placeholder={
                  <div className="flex flex-col items-center space-y-3">
                    <div className="w-12 h-12 border-3 border-primary-400 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-gray-400 text-sm">Loading chairman image...</p>
                  </div>
                }
              />
            </div>
          </motion.div>

          {/* Message Content */}
          <motion.div
            variants={staggerItem}
            className="lg:col-span-2"
          >
            <Card className="p-8 lg:p-12 relative overflow-hidden">
              <div className="absolute top-4 right-4 w-16 h-1 bg-primary-400"></div>

              <div className="relative z-10">
                <motion.div
                  variants={fadeInUp}
                  className="mb-8"
                >
                  <div className="flex items-start mb-6">
                    <div className="text-6xl text-primary-400/30 leading-none mr-4 font-serif">"</div>
                    <div>
                      <p className={`${colors.text.secondary} text-lg leading-relaxed mb-6`}>
                        At Julfikar Steel, we believe that steel is more than just a material—it is the backbone of progress, strength, and development. Since our inception, our focus has been on delivering uncompromising quality, innovative solutions, and reliable supply to support projects that shape the future of our nation.
                      </p>
                      <p className={`${colors.text.secondary} text-lg leading-relaxed mb-6`}>
                        As we move forward, we remain focused on our core values: strength, reliability, and excellence. Together, we will continue building a stronger tomorrow.
                      </p>
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  variants={fadeInUp}
                  className="border-l-4 border-primary-400 pl-6"
                >
                  <h3 className={`text-2xl font-bold ${colors.text.primary} mb-2`}>
                    Md. Mahbubur Rashid Jewel
                  </h3>
                  <p className="text-primary-400 font-semibold text-lg mb-1">Chairman</p>
                  <p className={`${colors.text.muted} text-sm`}>Julfikar Steel Ltd.</p>
                </motion.div>

                {/* Decorative Elements */}
                <div className="absolute -top-4 -left-4 w-32 h-32 bg-primary-400/5 rounded-full blur-xl"></div>
                <div className="absolute -bottom-8 -right-8 w-40 h-40 bg-primary-400/5 rounded-full blur-2xl"></div>
              </div>
            </Card>
          </motion.div>
        </motion.div>

        {/* Company Values Section */}
        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.3 }}
          className="mt-20"
        >
          <motion.div
            variants={fadeInUp}
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
          >
            {[
              {
                title: "Strength",
                description: "Building robust foundations for tomorrow's infrastructure",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 11.172V5L8 4z" />
                  </svg>
                )
              },
              {
                title: "Reliability",
                description: "Consistent quality and timely delivery you can depend on",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                )
              },
              {
                title: "Excellence",
                description: "Exceeding expectations through innovation and dedication",
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                  </svg>
                )
              }
            ].map((value, index) => (
              <motion.div
                key={value.title}
                variants={staggerItem}
                className="text-center"
              >
                <Card className="p-6 h-full hover:shadow-2xl transition-all duration-300 group">
                  <div className="text-primary-400 mb-4 flex justify-center group-hover:scale-110 transition-transform duration-300">
                    {value.icon}
                  </div>
                  <h3 className={`text-xl font-bold ${colors.text.primary} mb-3`}>
                    {value.title}
                  </h3>
                  <p className={`${colors.text.secondary} text-sm leading-relaxed`}>
                    {value.description}
                  </p>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </Section>
  );
};

export default Chairman;